// Counts real ad producing bundles, not just visual variations.

const ASPECT_RATIO_TOLERANCE = 0.01 // +/-1%, matches display_asset_specs.py

const DISPLAY_IMAGE_ASPECT_RATIOS = {
  MARKETING_IMAGE: 1.91 / 1.0,
  SQUARE_MARKETING_IMAGE: 1.0 / 1.0,
  PORTRAIT_MARKETING_IMAGE: 4.0 / 5.0,
  TALL_PORTRAIT_MARKETING_IMAGE: 9.0 / 16.0,
}

const DEMAND_GEN_VIDEO_ASPECT_RATIOS = {
  LANDSCAPE: 16.0 / 9.0,
  SQUARE: 1.0 / 1.0,
  VERTICAL: 9.0 / 16.0,
}

const MAX_ADS_PER_AD_GROUP = 50

const MAX_HEADLINE_LEN = 30
const MAX_LONG_HEADLINE_LEN = 90
const MAX_DESCRIPTION_LEN = 90
const MAX_DEMAND_GEN_HEADLINE_LEN = 40

const SHORT_HEADLINE_CAP_BY_CAMPAIGN_TYPE = {
  DISPLAY: MAX_HEADLINE_LEN,
  VIDEO_CONVERSIONS: MAX_DEMAND_GEN_HEADLINE_LEN,
}

export const GoogleAdType = {
  DISPLAY: 'DisplayAdConfig',
  DEMAND_GEN_VIDEO: 'DemandGenVideoAdConfig',
  DEMAND_GEN_MULTI: 'DemandGenMultiAssetAdConfig',
  DEMAND_GEN_CAROUSEL: 'DemandGenCarouselAdConfig',
}

// -- media classification (mirrors media_validation.py) --------------------

export function isYoutubeMediaItem(item) {
  return Boolean(item?.youtube_video_id)
}

export function isVideoMediaItem(item) {
  if (isYoutubeMediaItem(item)) return true
  const type = item?.type || ''
  return type.startsWith('video') || type === 'youtube'
}

// Mirrors classify_display_image_asset_type: genuine aspect-ratio fit only
// (+/-1%), across all 4 Display/Demand-Gen image slots. Missing width/height
// defaults to MARKETING_IMAGE (same as the backend — an unmeasured image
// can't be shown to violate a ratio it was never measured against).
export function classifyDisplayImageAssetType(width, height) {
  if (!width || !height) return 'MARKETING_IMAGE'
  const ratio = width / height
  for (const [assetType, targetRatio] of Object.entries(DISPLAY_IMAGE_ASPECT_RATIOS)) {
    if (Math.abs(ratio - targetRatio) / targetRatio <= ASPECT_RATIO_TOLERANCE) return assetType
  }
  return null
}

// Mirrors classify_demand_gen_video_ratio: unlike images, an unmeasured
// video is never guessed into a slot — returns null (unclassifiable).
export function classifyDemandGenVideoRatio(width, height) {
  if (!width || !height) return null
  const ratio = width / height
  for (const [label, targetRatio] of Object.entries(DEMAND_GEN_VIDEO_ASPECT_RATIOS)) {
    if (Math.abs(ratio - targetRatio) / targetRatio <= ASPECT_RATIO_TOLERANCE) return label
  }
  return null
}

// Mirrors _enrich_media_items(strict=True): video items pass through
// unchanged; image items with no genuine Display aspect-ratio fit are
// dropped rather than force-tagged.
function enrichMediaItems(items) {
  const result = []
  for (const item of items) {
    if (!item) continue
    if (isVideoMediaItem(item)) {
      result.push(item)
      continue
    }
    if (item.asset_type) {
      result.push(item)
      continue
    }
    const assetType = classifyDisplayImageAssetType(item.width, item.height)
    if (assetType === null) continue
    result.push({ ...item, asset_type: assetType })
  }
  return result
}

// Mirrors _select_ad_type: DISPLAY campaigns always get DisplayAdConfig;
// Demand Gen prefers carousel, then video, else the multi-asset image ad.
export function selectAdType(campaignType, mediaItems, hasCarousel) {
  if (campaignType === 'DISPLAY') return GoogleAdType.DISPLAY
  if (hasCarousel) return GoogleAdType.DEMAND_GEN_CAROUSEL
  const hasVideos = (mediaItems || []).some(isVideoMediaItem)
  if (hasVideos) return GoogleAdType.DEMAND_GEN_VIDEO
  return GoogleAdType.DEMAND_GEN_MULTI
}

// -- shared helpers ----------------------------------------------------

function stampIndices(items, key) {
  return (items || []).map((item, i) => ({ ...item, [key]: item[key] ?? i }))
}

function refIdx(item) {
  return item.media_index ?? item.pm_index ?? 0
}

// Groups items sharing an explicit placement_group_id (back-compat manual
// grouping, still reachable via the Media UI's placement-group feature).
// Returns { groups: Map<gid, items[]>, uncovered: items[] }.
function splitExplicitGroups(allItems) {
  const groups = new Map()
  const covered = new Set()
  allItems.forEach((item, idx) => {
    const gid = item.placement_group_id
    if (gid) {
      if (!groups.has(gid)) groups.set(gid, [])
      groups.get(gid).push(item)
      covered.add(idx)
    }
  })
  const uncovered = allItems.filter((_, idx) => !covered.has(idx))
  return { groups, uncovered }
}

// min(pool, key=...) with Python's stable tie-break: first item with the
// strictly-smallest key wins.
function minBy(pool, keyFn) {
  let best = null
  let bestKey = null
  for (const item of pool) {
    const k = keyFn(item)
    if (best === null || k < bestKey) {
      best = item
      bestKey = k
    }
  }
  return best
}

// -- Display media bundles (mirrors _build_display_media_bundles) ----------

function finalizeDisplayBundle(items) {
  const enriched = enrichMediaItems(items)
  const hasLandscape = enriched.some((i) => i.asset_type === 'MARKETING_IMAGE')
  const hasSquare = enriched.some((i) => i.asset_type === 'SQUARE_MARKETING_IMAGE')
  return hasLandscape && hasSquare
}

function autoPairDisplayBundleCount(uncovered) {
  const enriched = enrichMediaItems(uncovered)
  const landscapes = enriched.filter((i) => i.asset_type === 'MARKETING_IMAGE')
  const squares = enriched.filter((i) => i.asset_type === 'SQUARE_MARKETING_IMAGE')
  if (!landscapes.length || !squares.length) return 0
  // Driven by whichever pool is larger — 1:1 pairing, reusing the smaller
  // pool least-used-first — every uncovered image lands in exactly one
  // auto-formed bundle. Count only depends on the driving pool's size.
  return Math.max(landscapes.length, squares.length)
}

export function buildDisplayMediaBundleCount(mediaItems, placementMediaItems) {
  const media = stampIndices(mediaItems, 'media_index')
  const placementMedia = stampIndices(placementMediaItems, 'pm_index')
  const allItems = [...media, ...placementMedia]

  const { groups, uncovered } = splitExplicitGroups(allItems)
  let count = 0
  for (const items of groups.values()) {
    if (finalizeDisplayBundle(items)) count += 1
  }
  count += autoPairDisplayBundleCount(uncovered)
  return count
}

// -- Demand Gen multi-asset image bundles -----------------------------
// (mirrors _build_demand_gen_auto_placement_media)

// JS can't compare arrays with `<` like Python tuples — compare element-wise.
function tupleLess(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i]
  }
  return false
}

function minByTuple(pool, keyFn) {
  let best = null
  let bestKey = null
  for (const item of pool) {
    const k = keyFn(item)
    if (best === null || tupleLess(k, bestKey)) {
      best = item
      bestKey = k
    }
  }
  return best
}

function bestFromTuple(pool, usage, ref) {
  if (!pool.length) return null
  return minByTuple(pool, (p) => [usage.get(p.url) ?? 0, Math.abs(refIdx(p) - ref)])
}

function explicitDemandGenImageGroupIsValid(items) {
  const enriched = enrichMediaItems(items)
  return enriched.some(
    (i) => i.asset_type === 'MARKETING_IMAGE' || i.asset_type === 'SQUARE_MARKETING_IMAGE',
  )
}

// Returns the bundle count. Never throws — a group/pool with no valid
// landscape-or-square primary simply contributes 0, matching the real
// consequence (no image ad can be formed from it) without crashing this
// preview count on incomplete/invalid data.
export function buildDemandGenAutoPlacementMediaCount(mediaItems, placementMediaItems) {
  const media = stampIndices(mediaItems, 'media_index')
  const placementMedia = stampIndices(placementMediaItems, 'pm_index')
  const allItems = [...media, ...placementMedia]

  const { groups, uncovered } = splitExplicitGroups(allItems)
  let count = 0
  for (const items of groups.values()) {
    if (explicitDemandGenImageGroupIsValid(items)) count += 1
  }

  const enriched = enrichMediaItems(uncovered)
  const landscapes = enriched.filter((i) => i.asset_type === 'MARKETING_IMAGE')
  const squares = enriched.filter((i) => i.asset_type === 'SQUARE_MARKETING_IMAGE')
  const portraits = enriched.filter((i) => i.asset_type === 'PORTRAIT_MARKETING_IMAGE')
  const tallPortraits = enriched.filter((i) => i.asset_type === 'TALL_PORTRAIT_MARKETING_IMAGE')

  // A portrait/tall-portrait-only pool (no landscape or square anywhere,
  // and no valid bundle formed yet from an explicit group) can't form any
  // valid Demand Gen image ad — Phase A below would otherwise blindly
  // build an incomplete bundle out of just a portrait.
  if (
    (portraits.length || tallPortraits.length) &&
    !landscapes.length &&
    !squares.length &&
    count === 0
  ) {
    return 0
  }

  const landscapeUsage = new Map(landscapes.map((lg) => [lg.url, 0]))
  const squareUsage = new Map(squares.map((sq) => [sq.url, 0]))

  // Phase A: one bundle per portrait, pairing in the nearest unused
  // tall-portrait when one exists. Primaries are reused (usage tracked only
  // for even distribution, not exclusion).
  const tallPortraitsRemaining = [...tallPortraits]
  for (const pt of portraits) {
    const ref = refIdx(pt)
    let tallPt = null
    if (tallPortraitsRemaining.length) {
      tallPt = minBy(tallPortraitsRemaining, (t) => Math.abs(refIdx(t) - ref))
      tallPortraitsRemaining.splice(tallPortraitsRemaining.indexOf(tallPt), 1)
    }
    const lg = bestFromTuple(landscapes, landscapeUsage, ref)
    const sq = bestFromTuple(squares, squareUsage, ref)
    if (lg) landscapeUsage.set(lg.url, (landscapeUsage.get(lg.url) ?? 0) + 1)
    if (sq) squareUsage.set(sq.url, (squareUsage.get(sq.url) ?? 0) + 1)
    count += 1
  }

  // Leftover tall-portraits (more tall-portraits than portraits) each get
  // their own bundle, same reuse logic.
  for (const tallPt of tallPortraitsRemaining) {
    const ref = refIdx(tallPt)
    const lg = bestFromTuple(landscapes, landscapeUsage, ref)
    const sq = bestFromTuple(squares, squareUsage, ref)
    if (lg) landscapeUsage.set(lg.url, (landscapeUsage.get(lg.url) ?? 0) + 1)
    if (sq) squareUsage.set(sq.url, (squareUsage.get(sq.url) ?? 0) + 1)
    count += 1
  }

  // Phase B: unused primaries form solo/paired bundles.
  const unusedLgs = landscapes.filter((lg) => (landscapeUsage.get(lg.url) ?? 0) === 0)
  const unusedSqs = squares.filter((sq) => (squareUsage.get(sq.url) ?? 0) === 0)
  const usedSqUrls = new Set()

  const sortedUnusedLgs = [...unusedLgs].sort((a, b) => refIdx(a) - refIdx(b))
  for (const lg of sortedUnusedLgs) {
    const candidates = unusedSqs.filter((sq) => !usedSqUrls.has(sq.url))
    const sq = candidates.length ? minBy(candidates, (s) => Math.abs(refIdx(s) - refIdx(lg))) : null
    if (sq) usedSqUrls.add(sq.url)
    count += 1
  }

  for (const sq of unusedSqs) {
    if (!usedSqUrls.has(sq.url)) count += 1
  }

  return count
}

// -- Demand Gen video bundles (mirrors _build_demand_gen_video_bundles) ----

const _DEMAND_GEN_VIDEO_RATIOS = ['LANDSCAPE', 'SQUARE', 'VERTICAL']

export function buildDemandGenVideoBundleCount(mediaItems, placementMediaItems) {
  const media = stampIndices(mediaItems, 'media_index')
  const placementMedia = stampIndices(placementMediaItems, 'pm_index')
  const allItems = [...media, ...placementMedia]
  const videoItems = allItems.filter((i) => Boolean(i.youtube_video_id))

  const explicit = new Map()
  const covered = new Set()
  for (const item of videoItems) {
    const gid = item.placement_group_id
    if (gid) {
      if (!explicit.has(gid)) explicit.set(gid, [])
      explicit.get(gid).push(item)
      covered.add(item)
    }
  }

  // Explicit groups each form one bundle (a >3-video group would fail real
  // backend validation at boost time — out of scope for this preview count,
  // which still counts it as one ad slot).
  let count = explicit.size

  const uncovered = videoItems.filter((i) => !covered.has(i))
  const classified = []
  const unclassifiable = []
  for (const item of uncovered) {
    const ratio = classifyDemandGenVideoRatio(item.width, item.height)
    if (ratio) classified.push({ item, ratio })
    else unclassifiable.push(item)
  }

  const pools = { LANDSCAPE: [], SQUARE: [], VERTICAL: [] }
  for (const { item, ratio } of classified) pools[ratio].push(item)
  for (const ratio of _DEMAND_GEN_VIDEO_RATIOS) pools[ratio].sort((a, b) => refIdx(a) - refIdx(b))

  const usage = {}
  for (const ratio of _DEMAND_GEN_VIDEO_RATIOS) {
    usage[ratio] = new Map(pools[ratio].map((item) => [item.youtube_video_id, 0]))
  }

  const bestForRatio = (ratio, ref) => {
    const pool = pools[ratio]
    if (!pool.length) return null
    return minByTuple(pool, (p) => [
      usage[ratio].get(p.youtube_video_id) ?? 0,
      Math.abs(refIdx(p) - ref),
    ])
  }

  // Driving ratio = the pool with the most videos, ties favouring
  // LANDSCAPE, then SQUARE, then VERTICAL (fixed iteration order below).
  let drivingRatio = _DEMAND_GEN_VIDEO_RATIOS[0]
  for (const ratio of _DEMAND_GEN_VIDEO_RATIOS) {
    if (pools[ratio].length > pools[drivingRatio].length) drivingRatio = ratio
  }

  for (const driver of pools[drivingRatio]) {
    const ref = refIdx(driver)
    usage[drivingRatio].set(
      driver.youtube_video_id,
      (usage[drivingRatio].get(driver.youtube_video_id) ?? 0) + 1,
    )
    for (const ratio of _DEMAND_GEN_VIDEO_RATIOS) {
      if (ratio === drivingRatio) continue
      const pick = bestForRatio(ratio, ref)
      if (pick)
        usage[ratio].set(pick.youtube_video_id, (usage[ratio].get(pick.youtube_video_id) ?? 0) + 1)
    }
    count += 1
  }

  // Videos with no classifiable ratio each get their own single-video
  // bundle rather than being blind-chunked by array position.
  count += unclassifiable.length

  return count
}

// -- Demand Gen carousel groups (mirrors _distinct_carousel_groups) --------

export function distinctCarouselGroupCount(carouselCards) {
  const seen = new Set()
  for (const card of carouselCards || []) {
    const gid = card?.carousel_group_id
    if (gid) seen.add(gid)
  }
  return seen.size
}

// -- ad_type fan-out + dimension product (mirrors ad_types_for_creative /
// _dims_for_ad_type / count_natural_variations in orchestrator.py) --------

function cappedLen(arr, cap) {
  return Math.min((arr || []).length, cap)
}

export function classifyHeadlines(rawHeadlines, shortCap) {
  const headlines = rawHeadlines || []
  const short = headlines.filter((h) => h.length <= shortCap)
  const longSuitable = headlines.filter(
    (h) => h.length > shortCap && h.length <= MAX_LONG_HEADLINE_LEN,
  )
  return [short, longSuitable.length ? longSuitable : short]
}

function shortHeadlineCap(campaignType) {
  return SHORT_HEADLINE_CAP_BY_CAMPAIGN_TYPE[campaignType] ?? MAX_HEADLINE_LEN
}

function headlineCount(creative, campaignType) {
  const [short] = classifyHeadlines(creative.headline, shortHeadlineCap(campaignType))
  return cappedLen(short, 5)
}

function descriptionCount(creative) {
  const bodies = creative.body?.length
    ? creative.body
    : creative.description
      ? [creative.description]
      : []
  const usable = bodies.filter((b) => b.length <= MAX_DESCRIPTION_LEN)
  return cappedLen(usable.length ? usable : bodies, 5)
}

function longHeadlineCount(creative, campaignType) {
  if (creative.long_headline?.length) return cappedLen(creative.long_headline, 5)
  const [, long] = classifyHeadlines(creative.headline, shortHeadlineCap(campaignType))
  return cappedLen(long, 5)
}

function finalUrlCount(creative) {
  return (creative.destination_url || []).length
}

// Every ad_type this creative should produce ads for, and the bundle count
// backing each ad_type's media dimension. A Demand Gen creative with both a
// video and qualifying images fans out into two ad_types —
// one video ad, one image ad.
export function adTypesAndMediaCountsForCreative(creative, campaignType) {
  const mediaItems = creative.media || []
  const placementMediaItems = creative.placement_media || []
  const hasCarousel = Boolean(creative.carousel_cards?.length)
  const adType = selectAdType(campaignType, mediaItems, hasCarousel)

  const adTypes = [adType]
  const mediaCounts = {}

  if (adType === GoogleAdType.DISPLAY) {
    mediaCounts[GoogleAdType.DISPLAY] = buildDisplayMediaBundleCount(
      mediaItems,
      placementMediaItems,
    )
  } else if (adType === GoogleAdType.DEMAND_GEN_MULTI) {
    mediaCounts[GoogleAdType.DEMAND_GEN_MULTI] = buildDemandGenAutoPlacementMediaCount(
      mediaItems,
      placementMediaItems,
    )
  } else if (adType === GoogleAdType.DEMAND_GEN_VIDEO) {
    mediaCounts[GoogleAdType.DEMAND_GEN_VIDEO] = buildDemandGenVideoBundleCount(
      mediaItems,
      placementMediaItems,
    )
    const imageBundleCount = buildDemandGenAutoPlacementMediaCount(mediaItems, placementMediaItems)
    if (imageBundleCount > 0) {
      adTypes.push(GoogleAdType.DEMAND_GEN_MULTI)
      mediaCounts[GoogleAdType.DEMAND_GEN_MULTI] = imageBundleCount
    }
  } else if (adType === GoogleAdType.DEMAND_GEN_CAROUSEL) {
    mediaCounts[GoogleAdType.DEMAND_GEN_CAROUSEL] = distinctCarouselGroupCount(
      creative.carousel_cards,
    )
  }

  return { adTypes, mediaCounts }
}

// Mirrors _carousel_ad_headline_fallback: a carousel whose headlines live only
// on its cards still gets one ad-level headline when it is translated for Google.
function carouselAdHeadlineFallback(creative) {
  const cardHeadlines = (creative.carousel_cards || [])
    .map((card) => card?.headline)
    .filter((headline) => typeof headline === 'string' && headline.trim())
  if (!cardHeadlines.length) return null
  const fitting = cardHeadlines.find((headline) => headline.length <= MAX_DEMAND_GEN_HEADLINE_LEN)
  if (fitting) return fitting
  const businessName = (creative.business_name || '').trim()
  return businessName && businessName.length <= MAX_DEMAND_GEN_HEADLINE_LEN ? businessName : null
}

function carouselHeadlineCount(creative, campaignType) {
  return headlineCount(creative, campaignType) || (carouselAdHeadlineFallback(creative) ? 1 : 0)
}

function dimsForAdType(adType, creative, mediaCounts, campaignType) {
  const mediaCount = mediaCounts[adType] || 0
  if (adType === GoogleAdType.DEMAND_GEN_CAROUSEL) {
    return [
      carouselHeadlineCount(creative, campaignType),
      descriptionCount(creative),
      mediaCount,
      finalUrlCount(creative),
    ]
  }
  return [
    headlineCount(creative, campaignType),
    descriptionCount(creative),
    longHeadlineCount(creative, campaignType),
    mediaCount,
    finalUrlCount(creative),
  ]
}

export function countNaturalVariations(creative, campaignType) {
  if (!creative) return 0
  const excluded = new Set(creative.excluded_variations || [])
  const { adTypes, mediaCounts } = adTypesAndMediaCountsForCreative(creative, campaignType)

  let total = 0
  for (const adType of adTypes) {
    const dims = dimsForAdType(adType, creative, mediaCounts, campaignType)
    if (dims.some((d) => d === 0)) continue
    total += dims.reduce((a, b) => a * b, 1)
  }

  return Math.max(0, Math.min(MAX_ADS_PER_AD_GROUP, total - excluded.size))
}
